import React, { useEffect, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { LeftPanel } from '../components/LeftPanel';
import { FleetMap } from '../components/FleetMap';
import { DriverDetail } from '../components/DriverDetail';
import { DeliveryTimeline } from '../components/DeliveryTimeline';
import { DemoControls } from '../components/DemoControls';
import { DesignNotes } from '../components/DesignNotes';
import { mockDrivers, mockAlerts, mockDeliveries, mockTriageItems } from '../data/mockData';
import { AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Dashboard() {
  const [demoState, setDemoState] = useState<'normal' | 'hos' | 'offline' | 'bridge'>('normal');
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [highlightedDriverIds, setHighlightedDriverIds] = useState<string[] | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'h' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        setShowHidden(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  // Handle demo state changes
  useEffect(() => {
    if (demoState === 'hos') {
      setSelectedDriverId('d-marcus');
      setSelectedAlertId('a-hos-marcus');
    } else if (demoState === 'offline') {
      setSelectedDriverId('d-sarah');
      setSelectedAlertId('a-off-sarah');
    } else if (demoState === 'bridge') {
      setSelectedDriverId('d-david');
      setSelectedAlertId('a-brg-david');
    } else {
      setSelectedDriverId(null);
      setSelectedAlertId(null);
      setHighlightedDriverIds(null);
    }
  }, [demoState]);
  const handleSelectAlert = (alertId: string, driverId: string) => {
    setSelectedAlertId(alertId);
    const alert = mockAlerts.find(a => a.id === alertId);
    if (alert?.affectedDriverIds && alert.affectedDriverIds.length > 0) {
      setHighlightedDriverIds(alert.affectedDriverIds);
      setSelectedDriverId(null);
    } else {
      setHighlightedDriverIds(null);
      setSelectedDriverId(driverId);
    }
  };
  const [scrollToRouteTs, setScrollToRouteTs] = useState<number | null>(null);
  const [selectedDeliveryRef, setSelectedDeliveryRef] = useState<string | null>(null);
  const handleSelectDriver = (driverId: string) => {
    setSelectedDriverId(driverId);
    setHighlightedDriverIds(null);
    setScrollToRouteTs(null);
    setSelectedDeliveryRef(null);
    // Find if there's an active alert for this driver
    const alert = mockAlerts.find(a => a.driverId === driverId);
    setSelectedAlertId(alert ? alert.id : null);
  };
  const handleSelectDeliveryDriver = (driverId: string, deliveryRef?: string) => {
    setSelectedDriverId(driverId);
    setHighlightedDriverIds(null);
    setScrollToRouteTs(Date.now());
    setSelectedDeliveryRef(deliveryRef || null);
    const alert = mockAlerts.find(a => a.driverId === driverId);
    setSelectedAlertId(alert ? alert.id : null);
  };
  const [alertFilterRequest, setAlertFilterRequest] = useState<{
    severity: 'all' | 'critical' | 'warning' | 'low';
    ts: number;
  } | null>(null);
  const handleFilterAlerts = (severity: 'critical' | 'warning' | 'low' | 'all') => {
    setAlertFilterRequest({
      severity,
      ts: Date.now()
    });
  };
  const selectedDriver = mockDrivers.find(d => d.id === selectedDriverId) || null;
  const driverAlerts = selectedDriverId ? mockAlerts.filter(a => a.driverId === selectedDriverId || a.affectedDriverIds?.includes(selectedDriverId)) : [];
  return <div className="flex flex-col h-full w-full bg-slate-950 overflow-hidden" data-id="element-488">
      {/* Global Toast for Critical States */}
      <AnimatePresence data-id="element-489">
        {demoState === 'hos' && <motion.div initial={{
        y: -100
      }} animate={{
        y: 0
      }} exit={{
        y: -100
      }} className="absolute top-0 left-0 right-0 z-50 flex justify-center pt-4 pointer-events-none" data-id="element-490">
            <div className="bg-brand-red text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 font-medium text-sm" data-id="element-491">
              <AlertTriangle className="w-5 h-5" data-id="element-492" />
              URGENT: Driver Marcus Johnson approaching mandatory HOS reset — 3
              deliveries need reassignment
            </div>
          </motion.div>}
      </AnimatePresence>

      <TopBar onFilterAlerts={handleFilterAlerts} data-id="element-493" />

      <div className="flex-1 overflow-hidden relative" data-id="element-494">
        {/* Map fills entire area */}
        <div className="absolute inset-0 z-10" data-id="element-495">
          <FleetMap drivers={mockDrivers} selectedDriverId={selectedDriverId} highlightedDriverIds={highlightedDriverIds} onSelectDriver={handleSelectDriver} onDeselect={() => {
          setSelectedDriverId(null);
          setSelectedAlertId(null);
          setHighlightedDriverIds(null);
        }} demoState={demoState} data-id="element-496" />
        </div>

        {/* Left panel overlays on top of map */}
        <div className="absolute left-0 top-0 bottom-0 z-20 pointer-events-none" data-id="element-497">
          <div className="pointer-events-auto h-full" data-id="element-498">
            <LeftPanel alerts={mockAlerts} drivers={mockDrivers} deliveries={mockDeliveries} triageItems={mockTriageItems} selectedAlertId={selectedAlertId} selectedDriverId={selectedDriverId} onSelectAlert={handleSelectAlert} onSelectDriver={handleSelectDriver} onSelectDeliveryDriver={handleSelectDeliveryDriver} requestedAlertFilter={alertFilterRequest} data-id="element-499" />
          </div>
        </div>

        {/* Driver detail overlays on right */}
        <AnimatePresence data-id="element-500">
          {selectedDriver && <div className="absolute right-0 top-0 bottom-0 z-20" data-id="element-501">
              <DriverDetail driver={selectedDriver} alerts={driverAlerts} deliveries={mockDeliveries.filter(d => d.driverId === selectedDriverId)} scrollToRouteTs={scrollToRouteTs} selectedDeliveryRef={selectedDeliveryRef} data-id="element-502" />
            </div>}
        </AnimatePresence>
      </div>

      {showHidden && <>
          <DemoControls currentState={demoState} onStateChange={setDemoState} data-id="element-503" />
          <DesignNotes data-id="element-504" />
        </>}
    </div>;
}